/**
 * src/components/ui/DateTimeFields.jsx — labelled date and time pickers.
 *
 * Real native `<input type="date">`/`<input type="time">`, not a custom
 * calendar/clock widget — per REDESIGN.md's preference for native elements,
 * this gets the OS's own date/time picker (which already handles locale,
 * keyboard entry, and screen-reader semantics correctly) for free. The
 * existing booking-slot picker (`.slot`/`.slot-grid` in styles.css) is a
 * distinct pattern — choosing a specific bookable slot from a studio's real
 * availability — and is left alone; these are for plain "pick a date" /
 * "pick a time" inputs elsewhere (e.g. a future filter or settings field).
 */
import { Field, syncAriaInvalid } from './Field.jsx';
import { openNativePicker } from '../../lib/picker.js';

/**
 * @param {{ label: string, hint?: React.ReactNode, error?: string|null, required?: boolean, id?: string } & React.InputHTMLAttributes<HTMLInputElement>} props
 * @returns {JSX.Element}
 */
export function DateField({ label, hint, error, required, id, onBlur, onInput, onClick, ...inputProps }) {
  return (
    <Field label={label} hint={hint} error={error} required={required} htmlFor={id}>
      {({ inputId, hintId, errorId }) => (
        <input
          type="date"
          id={inputId}
          required={required}
          aria-describedby={hintId}
          aria-errormessage={error ? errorId : undefined}
          aria-invalid={error ? 'true' : undefined}
          onBlur={(e) => {
            syncAriaInvalid(e);
            onBlur?.(e);
          }}
          onInput={(e) => {
            syncAriaInvalid(e);
            onInput?.(e);
          }}
          onClick={(e) => {
            openNativePicker(e);
            onClick?.(e);
          }}
          {...inputProps}
        />
      )}
    </Field>
  );
}

/**
 * @param {{ label: string, hint?: React.ReactNode, error?: string|null, required?: boolean, id?: string } & React.InputHTMLAttributes<HTMLInputElement>} props
 * @returns {JSX.Element}
 */
export function TimeField({ label, hint, error, required, id, onBlur, onInput, onClick, ...inputProps }) {
  return (
    <Field label={label} hint={hint} error={error} required={required} htmlFor={id}>
      {({ inputId, hintId, errorId }) => (
        <input
          type="time"
          id={inputId}
          required={required}
          aria-describedby={hintId}
          aria-errormessage={error ? errorId : undefined}
          aria-invalid={error ? 'true' : undefined}
          onBlur={(e) => {
            syncAriaInvalid(e);
            onBlur?.(e);
          }}
          onInput={(e) => {
            syncAriaInvalid(e);
            onInput?.(e);
          }}
          onClick={(e) => {
            openNativePicker(e);
            onClick?.(e);
          }}
          {...inputProps}
        />
      )}
    </Field>
  );
}
